import { useEffect } from 'react'
import { createGlobalState, useCookie } from 'react-use'
import { query } from 'utils/sanity/query'
import isDefined from 'utils/is-defined'

export const PREFERRED_LOCATION_COOKIE = 'hut_preferred_location_id'

/**
 * @typedef PreferredLocation
 * @property {string} address
 * @property {string} city
 * @property {string} directionsLink
 * @property {{lat: number, lng: number}} geolocation
 * @property {{
 *  sunday: string,
 *  monday: string,
 *  tuesday: string,
 *  wednesday: string,
 *  thursday: string,
 *  friday: string,
 *  saturday: string
 * }} hours
 * @property {string} locationNumber
 * @property {string} phoneNumber
 * @property {string} slug
 * @property {{
 *  date: string,
 *  hours: string,
 *  title: string
 * }[]} specialHours
 * @property {string} state
 * @property {('open'|'temporarily-closed'|'permanently-closed')} status
 * @property {string} title
 * @property {number} zip
 */

/**
 * @type {() => [?PreferredLocation, (value: PreferredLocation) => void]}
 */
const useGlobalValue = createGlobalState(null)

export const usePreferredLocation = () => {
  const [locationDetails, setLocationDetails] = useGlobalValue()
  const [value, updateCookie] = useCookie(PREFERRED_LOCATION_COOKIE)

  useEffect(() => {
    if (isDefined(value) && locationDetails?.locationNumber !== Number(value)) {
      query(
        `
          *[_type == "location" && locationNumber == $locationNumber && status != "permanently-closed" && retailLocation == true]{
            address,
            city,
            geolocation {
              lat,
              lng
            },
            "directionsLink": "https://www.google.com/maps/dir//Hutson+Inc+" + city + "+" + state,
            hours {
              sunday,
              monday,
              tuesday,
              wednesday,
              thursday,
              friday,
              saturday
            },
            locationNumber,
            phoneNumber,
            "slug": "/locations/" + handle + "/",
            specialHours {
              date,
              hours,
              title
            },
            state,
            status,
            title,
            zip
          }[0]
        `,
        { $locationNumber: value }
      )
        .then(res => {
          setLocationDetails(res.result)
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [value])

  /**
   * Update the preferred location
   * @param {number|string} locationNumber - Internal store id
   */
  const setPreferredLocation = locationNumber => {
    updateCookie(locationNumber)
  }

  return {
    preferredLocation: locationDetails,
    setPreferredLocation,
  }
}
